import { takeEvery, call, fork, select, all, put, delay } from 'redux-saga/effects';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _mapValues from 'lodash/mapValues';
import _set from 'lodash/set';
import { parseISO } from 'date-fns';
import parse from 'date-fns/parse';
import format from 'date-fns/format';

import { FULL_ISO_DATE_FORMAT } from 'constants/logic';
import URLS from 'constants/urls';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { transformKeysToCamelCase, transformKeysToSnakeCase, trimTimezone } from 'helpers/core';
import { trackClassScheduleAdd } from 'helpers/events';
import { makePath } from 'helpers/link-generation';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { closeModal, waitForModal } from 'components/Modal';

import {
  schedulesData,
  scheduleData,
  scheduleInstance,
  schedulesInstances,
  schedulesClasses,
  scheduleClass,
  instructorData,
  instructorsData,
  msInventoryVenues,
} from 'store/saga/entities/bound';
import {
  selectActiveVenue,
  selectActiveVenueId,
  selectVenueByVenueId,
  selectRouteParams,
  selectRouteQuery,
  selectSchedulesStartDate,
} from 'selectors/general';
import { setSchedulesStartDate } from 'reducers/app';

import { waitForAppInit, ACTIVE_VENUE_CHANGED, getStartDateOrToday } from 'features/App/App.saga';
import { reportError } from 'helpers/error-logging';

import { PUBLISHED_STATUS } from './Schedules.helpers';
import {
  setSelectedScheduleInstanceId,
  setCreatedClassId,
  setCreatedInstructorId,
  setNextDate,
  SET_SCHEDULES_BOARD_STATUS_FILTERS,
  SET_SCHEDULES_BOARD_TYPE_FILTERS,
  SET_SCHEDULES_BOARD_CLASS_FILTERS,
  SET_SCHEDULES_BOARD_DURATIONS_FILTERS,
  SET_SCHEDULES_BOARD_INSTRUCTOR_FILTERS,
} from './Schedules.reducers';
import {
  selectSchedulesBoardFiltersQueries,
  selectFirstInstanceOfSchedule,
  selectSchedulesInstances,
  selectSchedulesBoardNextDate,
  selectSelectedScheduleInstanceId,
} from './Schedules.selectors';
import messages from './Schedules.messages';
import { ROSTER_PAGE_SOURCE } from '../YieldManagement/YieldManagement.saga';
import { selectActiveScheduleId } from '../YieldManagement/YieldManagement.selectors';

export const FETCH_SCHEDULE = 'FETCH_SCHEDULE';
export const FETCH_SCHEDULES_INSTANCES = 'FETCH_INSTANCES';
export const FETCH_NEXT_SCHEDULES_INSTANCES = 'FETCH_NEXT_INSTANCES';
export const FETCH_SCHEDULES_CLASSES = 'FETCH_SCHEDULES_CLASSES';
export const CREATE_CLASS = 'CREATE_CLASS';
export const CREATE_INSTRUCTOR = 'CREATE_INSTRUCTOR';
export const CREATE_SCHEDULE = 'CREATE_SCHEDULE';
export const SELECT_SCHEDULE_ID = 'SELECT_SCHEDULE_ID';
export const SET_SELECTED_INSTANCE_ID = 'SET_SELECTED_INSTANCE_ID';
export const DELETE_SCHEDULE = 'DELETE_SCHEDULE';
export const FETCH_INSTRUCTORS = 'FETCH_INSTRUCTORS';
export const UPDATE_SCHEDULE_START_DATE = 'UPDATE_SCHEDULE_START_DATE';
export const UPDATE_SCHEDULE = 'UPDATE_SCHEDULE';
export const CANCEL_SCHEDULE_INSTANCE = 'CANCEL_SCHEDULE_INSTANCE';
export const UPDATE_SCHEDULE_INSTANCE = 'UPDATE_SCHEDULE_INSTANCE';

export const fetchSchedule = id => ({ type: FETCH_SCHEDULE, id });
export const fetchSchedulesInstances = ({ startDate, shouldRefreshData }) => ({
  type: FETCH_SCHEDULES_INSTANCES,
  startDate,
  shouldRefreshData,
});
export const setSelectedInstanceId = ({ instanceId, focus }) => ({
  type: SET_SELECTED_INSTANCE_ID,
  instanceId,
  focus,
});
export const fetchSchedulesClasses = () => ({ type: FETCH_SCHEDULES_CLASSES });
export const createClass = classData => ({ type: CREATE_CLASS, classData });
export const createInstructor = instructor => ({ type: CREATE_INSTRUCTOR, instructor });
export const createSchedule = (schedule, { rejectSubmit, resolveSubmit }) => ({
  type: CREATE_SCHEDULE,
  schedule,
  rejectSubmit,
  resolveSubmit,
});

export const updateSchedule = (schedule, shouldShowModal, { rejectSubmit, resolveSubmit }) => ({
  type: UPDATE_SCHEDULE,
  schedule,
  shouldShowModal,
  rejectSubmit,
  resolveSubmit,
});
export const selectScheduleId = scheduleId => ({ type: SELECT_SCHEDULE_ID, scheduleId });
export const deleteSchedule = scheduleId => ({ type: DELETE_SCHEDULE, scheduleId });
export const fetchInstructors = () => ({ type: FETCH_INSTRUCTORS });
export const updateStartDate = startDate => ({ type: UPDATE_SCHEDULE_START_DATE, startDate });
export const cancelScheduleInstance = instance => ({ type: CANCEL_SCHEDULE_INSTANCE, instance });
export const updateScheduleInstance = (instance, { rejectSubmit, resolveSubmit }) => ({
  type: UPDATE_SCHEDULE_INSTANCE,
  instance,
  rejectSubmit,
  resolveSubmit,
});

const trimTimezoneFromConflictedSchedules = conflictedSchedules =>
  conflictedSchedules && _mapValues(conflictedSchedules, schedule => schedule.map(date => trimTimezone(date)));

// TODO: don't make MSI calls for non-MSI venues
// TODO: once the BE correctly returns a 403, unwind this so 401s force login
const isVenueNotSupported = ({ response }) => response && (response.status === 401 || response.status === 403);
const isFormValidationError = ({ response }) => _get(response, 'data.error.key') === 'INVALID_FORM_DATA';
const isResultsInDuplicatesError = ({ response }) =>
  _get(response, 'data.form_fields_errors.start_date.0.key') === 'RECURRENCE_RESULTS_IN_DUPLICATES';

const isLastReservationError = ({ response }) =>
  _get(response, 'data.form_fields_errors.recurrence_end_date.0.key') === 'ACTIVE_RESERVATIONS';

const getErrorPathsForSet = (id = 0) => ({
  'capacity.0': 'capacity',
  'class_id.0': 'class_id',
  'duration_in_minutes.0': 'duration_in_minutes',
  'network_spots.0': 'network_spots',
  'recurrence.days.0': 'recurrence',
  'recurrence_end_date.0': 'recurrence_end_date',
  'recurring.0': 'schedule_type',
  [`start_and_duration.0.start_date.0`]: 'start_date',
  [`start_and_duration.${id}.duration_in_minutes.0`]: `start_and_duration.${id}.duration_in_minutes`,
  'start_date.0': 'start_date',
});

const fieldKeys = [
  'capacity',
  'class_id',
  'duration_in_minutes',
  'network_spots',
  'recurrence',
  'recurrence_end_date',
  'recurring',
  'start_and_duration',
  'start_date',
];

export const getValidationErrors = ({ response }) => response.data.form_fields_errors;

const getPathErrorArray = (obj, expectedKey, currentPath = []) => {
  const path = currentPath;
  Object.keys(obj).forEach(key => {
    if (key === expectedKey || (obj[key] && typeof obj[key] !== 'object')) {
      return path;
    }
    path.push(key);
    return getPathErrorArray(obj[key], key, path);
  });

  return path;
};

const getPathFromArray = (path = []) => path.join('.');

export const translateServerErrors = errors => {
  const errorsToTransaltion = errors;
  const parentKey = fieldKeys.find(key => errorsToTransaltion[key]);

  if (!parentKey) return;
  const childs = Object.keys(errorsToTransaltion[parentKey]);

  if (childs) {
    const newErrorsTranslation = {};
    childs.forEach(child => {
      const errorPathArray = getPathErrorArray(errorsToTransaltion[parentKey][child], 'key');
      const pathError = getPathFromArray([parentKey, child, ...errorPathArray]);
      const errorMessage = _get(errorsToTransaltion, pathError);
      const newPathError = getErrorPathsForSet(child)[getPathFromArray([parentKey, child, ...errorPathArray])];

      if (!_isEmpty(errorMessage) && newPathError) {
        const newErrorMessage =
          messages.saga.validationErrors[errorMessage.key] || messages.saga.validationErrors.INVALID_VALUE;
        _set(newErrorsTranslation, newPathError, newErrorMessage);
      }
    });

    return transformKeysToCamelCase(newErrorsTranslation);
  }
};

export function* refreshVenue(httpClient, venueId) {
  const url = URLS.venue.refresh(venueId);

  yield call([httpClient, 'get'], url);
}

export function* getSchedulePathFromPageSource(venueId, startDate) {
  let newPath;
  const { source } = yield select(selectRouteQuery);
  if (source === ROSTER_PAGE_SOURCE) {
    const scheduleId = yield select(selectActiveScheduleId);
    newPath = makePath.classes({ venueId, startDate, scheduleId, tab: 'roster', focus: 'list' });
  } else {
    newPath = makePath.schedules({ venueId, startDate });
  }
  return newPath;
}

export function* doFetchSchedule(client, action) {
  try {
    const { id } = action;
    const venueId = yield select(selectActiveVenueId);
    yield call(fetchEntity, scheduleData, [client, venueId, id]);
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.saga.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.saga.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doFetchSchedulesInstances(client, action) {
  try {
    const { shouldRefreshData } = action;
    let { startDate } = action;
    const { venueId, isMsInventoryVenue } = yield select(selectActiveVenue);
    const filter = yield select(selectSchedulesBoardFiltersQueries);

    if (!startDate) {
      startDate = yield select(selectSchedulesStartDate);
    }

    const nextDate = yield select(selectSchedulesBoardNextDate);
    const previousInstances = shouldRefreshData ? {} : yield select(selectSchedulesInstances);
    const pageDate = shouldRefreshData ? startDate : nextDate;

    if (isMsInventoryVenue) {
      const { nextDate: newNextDate } = yield call(fetchEntity, schedulesInstances, [
        client,
        venueId,
        filter,
        pageDate,
        previousInstances,
      ]);

      yield put(setNextDate(newNextDate));
    }
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.saga.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.saga.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doFetchSchedulesClasses(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    // Explicit `count` value since results can be more than the default of 100
    yield call(fetchEntity, schedulesClasses, [client, venueId, { count: 1000 }]);
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.saga.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.saga.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doCreateClass(client, action) {
  try {
    const venueId = yield select(selectActiveVenueId);
    const { classData } = action;
    const { id } = yield call(fetchEntity, scheduleClass, [client, venueId, classData]);
    if (classData.id) {
      yield put(createInfoBanner(messages.saga.success.classEdited));
    } else {
      yield put(createInfoBanner(messages.saga.success.classAdded));
    }
    yield put(closeModal());
    yield put(fetchSchedulesClasses());
    yield put(setCreatedClassId(id));
  } catch (error) {
    if (error.response?.status === 409) {
      yield put(createErrorBanner(messages.saga.error.classNameConflict));
    } else if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.saga.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.saga.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doCreateInstructor(client, action) {
  try {
    const { instructor } = action;
    const venueId = yield select(selectActiveVenueId);
    const { id: instructorId } = yield call(fetchEntity, instructorData, [client, venueId, null, instructor]);
    yield put(fetchInstructors());
    yield put(closeModal());
    yield put(setCreatedInstructorId(instructorId));
    yield put(createInfoBanner(messages.saga.success.instructorAdded));
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.saga.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.saga.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doUpdateStartDate(client, action) {
  const { httpClient } = client;
  const { startDate } = action;
  const venueId = yield select(selectActiveVenueId);

  yield all([
    put(setSchedulesStartDate(startDate)),
    call(doFetchSchedulesInstances, client, { shouldRefreshData: true, startDate }),
  ]);

  yield fork(refreshVenue, httpClient, venueId);

  const newPath = yield call(getSchedulePathFromPageSource, venueId, startDate);
  yield call(client.navigate, newPath);
}

export function* doCreateSchedule(client, action) {
  const { httpClient } = client;
  const { schedule, rejectSubmit, resolveSubmit } = action;
  try {
    const venueId = yield select(selectActiveVenueId);
    const { scheduleType, classId, instructorId, capacity, status } = schedule;

    const { id: scheduleId, startDate } = yield call(fetchEntity, schedulesData, [client, venueId, schedule]);
    yield call(trackClassScheduleAdd, { scheduleType, classId, instructorId, capacity, status });

    const parsedStartDate = parseISO(startDate);
    yield call(doFetchSchedulesInstances, client, { shouldRefreshData: true, startDate: parsedStartDate });

    const { id: instanceId } = yield select(selectFirstInstanceOfSchedule, scheduleId);

    if (instanceId) {
      yield put(setSelectedScheduleInstanceId({ instanceId }));
    }

    yield call(setSchedulesStartDate, parsedStartDate);
    yield call(resolveSubmit);
    yield put(createInfoBanner(messages.saga.success.scheduleAdded));

    yield fork(refreshVenue, httpClient, venueId);

    const newPath = makePath.schedules({ venueId, startDate: parsedStartDate });
    yield call(client.navigate, newPath);
    if (status === PUBLISHED_STATUS) {
      yield call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.CLASS_SCHEDULE_ADDED);
    }
  } catch (error) {
    if (isFormValidationError(error)) {
      if (isResultsInDuplicatesError(error)) {
        const conflictedSchedules = _get(error, 'response.data.form_fields_errors.start_date.0.data');

        yield all([
          call(rejectSubmit),
          call(waitForModal, client, 'ScheduleConflictsModal', {
            conflictedSchedules: trimTimezoneFromConflictedSchedules(conflictedSchedules),
          }),
        ]);
      } else {
        yield call(rejectSubmit, null, translateServerErrors(getValidationErrors(error)));
        yield put(createErrorBanner(messages.saga.error.problemWithRequest));
      }
    } else if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.saga.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.saga.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doUpdateSchedule(client, action) {
  const { httpClient } = client;
  const { schedule, rejectSubmit, resolveSubmit, shouldShowModal } = action;
  try {
    const venueId = yield select(selectActiveVenueId);
    const { startDate } = yield select(selectRouteParams);
    let confirmed = true;

    if (shouldShowModal) {
      confirmed = yield call(waitForModal, client, 'PublishScheduleDraftModal');
    }

    if (confirmed) {
      yield call(fetchEntity, schedulesData, [client, venueId, schedule]);
      const parsedStartDate = parseISO(startDate);
      yield call(doFetchSchedulesInstances, client, { shouldRefreshData: true, startDate: parsedStartDate });

      const instanceId = yield select(selectSelectedScheduleInstanceId);

      if (instanceId) {
        yield put(setSelectedScheduleInstanceId({ instanceId }));
      }

      yield call(setSchedulesStartDate, parsedStartDate);
      yield put(createInfoBanner(messages.saga.success.scheduleEdited));
      const newPath = yield call(getSchedulePathFromPageSource, venueId, parsedStartDate);
      yield call(client.navigate, newPath);
    }

    yield fork(refreshVenue, httpClient, venueId);

    yield call(resolveSubmit);
  } catch (error) {
    if (isFormValidationError(error)) {
      if (isResultsInDuplicatesError(error)) {
        const conflictedSchedules = _get(error, 'response.data.form_fields_errors.start_date.0.data');
        yield all([
          call(rejectSubmit),
          call(waitForModal, client, 'ScheduleConflictsModal', {
            conflictedSchedules: trimTimezoneFromConflictedSchedules(conflictedSchedules),
          }),
        ]);
      } else if (isLastReservationError(error)) {
        const lastReservationDateString = _get(
          error,
          'response.data.form_fields_errors.recurrence_end_date.0.reservation_datetime'
        );
        const lastReservationDate = parseISO(lastReservationDateString);

        yield put(createErrorBanner(messages.saga.error.lastReservationError, { lastReservationDate }));
      } else {
        yield call(rejectSubmit, null, translateServerErrors(getValidationErrors(error)));
        yield put(createErrorBanner(messages.saga.error.problemWithRequest));
      }
    } else if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.saga.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.saga.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doCancelScheduleInstance(client, action) {
  const { httpClient } = client;
  const { instance } = action;
  const { scheduleName, scheduleId, recurrenceDate } = instance;

  try {
    const venueId = yield select(selectActiveVenueId);
    const { startDate } = yield select(selectRouteParams);
    const endpoint = URLS.msSchedules.cancelOccurrence(venueId, scheduleId, recurrenceDate);
    yield call([httpClient, httpClient.put], endpoint);

    const parsedStartDate = parseISO(startDate);
    yield call(doFetchSchedulesInstances, client, { shouldRefreshData: true, startDate: parsedStartDate });

    yield call(setSchedulesStartDate, parsedStartDate);
    yield all([
      put(createInfoBanner(messages.saga.success.scheduleInstanceCancelled, { scheduleName })),
      call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.CLASS_CANCELLED),
    ]);

    yield fork(refreshVenue, httpClient, venueId);

    yield put(closeModal());
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.saga.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.saga.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doSetSelectedInstanceId(client, action) {
  const { startDate } = yield select(selectRouteParams);
  const { instanceId, focus } = action;
  const venueId = yield select(selectActiveVenueId);

  yield put(setSelectedScheduleInstanceId({ instanceId }));

  const pathArgs = {
    venueId,
    startDate: startDate ? parseISO(startDate) : null,
    focus,
  };
  const newPath = makePath.schedules(pathArgs);
  yield call(client.navigate, newPath);
}

export function* checkTerminationType(client, venueId, scheduleId) {
  try {
    const { httpClient } = client;

    const endpoint = URLS.msSchedules.checkTerminationType(venueId, scheduleId);
    const {
      data: { termination_type: terminationType },
    } = yield call([httpClient, httpClient.get], endpoint);

    // We should render delete button only for schedules that can be deleted
    // MS-1471: Remove these error messages
    if (terminationType === 'one_time_with_active_reservations') {
      yield put(createErrorBanner(messages.saga.error.deleteOneTimeScheduleWithActiveReservations));
      return;
    }

    if (terminationType === 'one_time_in_the_past') {
      yield put(createErrorBanner(messages.saga.error.deleteOneTimeScheduleInThePast));
      return;
    }

    return terminationType;
  } catch (error) {
    yield all([put(createErrorBanner(messages.saga.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doDeleteSchedule(client, action) {
  const { httpClient } = client;
  const { scheduleId } = action;

  try {
    const venueId = yield select(selectActiveVenueId);
    const startDate = new Date();
    const { startDate: scheduleDate } = yield select(selectRouteParams);
    const terminationType = yield call(checkTerminationType, client, venueId, scheduleId);
    if (!terminationType) return;

    const confirmed = yield call(waitForModal, client, 'DeleteScheduleModal', { terminationType });

    if (confirmed) {
      const isRemove = true;
      const { terminationType: scheduleTerminationType, recurrenceEndDate } = yield call(fetchEntity, schedulesData, [
        client,
        venueId,
        { id: scheduleId },
        isRemove,
      ]);

      yield put(setSchedulesStartDate(startDate));
      yield call(doFetchSchedulesInstances, client, { shouldRefreshData: true, startDate });

      const { id: instanceId } = yield select(selectFirstInstanceOfSchedule, scheduleId);
      if (instanceId) {
        yield put(setSelectedScheduleInstanceId({ instanceId }));
      }

      if (terminationType && messages.saga.terminationTypes[scheduleTerminationType]) {
        yield put(
          createInfoBanner(messages.saga.terminationTypes[scheduleTerminationType], {
            endDate: parseISO(recurrenceEndDate),
          })
        );
      } else {
        yield put(createInfoBanner(messages.saga.success.scheduleDeleted));
      }

      yield fork(refreshVenue, httpClient, venueId);

      const newPath = yield call(getSchedulePathFromPageSource, venueId, parseISO(scheduleDate));
      yield call(client.navigate, newPath);
    }
  } catch (error) {
    yield all([put(createErrorBanner(messages.saga.error.problemWithRequest)), call(reportError, error)]);
  }
}

export function* doUpdateScheduleInstance(client, action) {
  const { httpClient } = client;

  const { instance, rejectSubmit, resolveSubmit } = action;

  try {
    const { scheduleId, recurrenceDate, startDate, startTime, instructorId, classTitle } = instance;
    const venueId = yield select(selectActiveVenueId);
    const endpoint = URLS.msSchedules.updateOccurrence(venueId, scheduleId, recurrenceDate);
    yield call(
      [httpClient, httpClient.put],
      endpoint,
      transformKeysToSnakeCase({
        startDate: format(parse(startTime, 'HH:mm', parseISO(startDate)), FULL_ISO_DATE_FORMAT),
        instructorId,
      })
    );

    const parsedStartDate = parseISO(startDate);
    yield call(doFetchSchedulesInstances, client, { shouldRefreshData: true, startDate: parsedStartDate });
    yield all([
      call(setSchedulesStartDate, parsedStartDate),
      put(createInfoBanner(messages.saga.success.scheduleInstanceEdited, { scheduleName: classTitle })),
      call(resolveSubmit),
    ]);

    yield fork(refreshVenue, httpClient, venueId);

    const newPath = yield call(getSchedulePathFromPageSource, venueId, parsedStartDate);
    yield call(client.navigate, newPath);
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.saga.error.notSupported));
      yield call(rejectSubmit);
    } else {
      yield put(createErrorBanner(messages.saga.error.problemWithRequest));
      yield call(rejectSubmit);
    }
  }
}

export function* doFetchInstructors(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    yield call(fetchEntity, instructorsData, [client, venueId]);
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.saga.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.saga.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doFetchScheduleInstance(client, action) {
  try {
    const { scheduleId, occurrenceDate } = action;
    const venueId = yield select(selectActiveVenueId);
    yield call(fetchEntity, scheduleInstance, [client, venueId, scheduleId, occurrenceDate]);
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.saga.error.notSupported));
    } else {
      yield put(createErrorBanner(messages.saga.error.problemWithRequest));
    }
  }
}

export function* doFetchMsInventoryVenues(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    yield call(fetchEntityIfNeeded, msInventoryVenues, [client, venueId]);
  } catch (error) {
    if (isVenueNotSupported(error)) {
      yield put(createErrorBanner(messages.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.saga.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doClearScheduleFormState() {
  yield put(setCreatedClassId(null));
  yield put(setCreatedInstructorId(null));
}

export function* handleEditScheduleRouteEnter(client) {
  const { id } = yield select(selectRouteParams);
  yield call(doFetchSchedule, client, { id });
}

export function* handleEditScheduleInstanceRouteEnter(client) {
  const [{ id: scheduleId }, { occurrenceDate }] = yield all([select(selectRouteParams), select(selectRouteQuery)]);
  yield call(doFetchScheduleInstance, client, { scheduleId, occurrenceDate });
}

export function* handleRouteEnterForm(client) {
  const routeParams = yield select(selectRouteParams);
  const activeVenue = yield select(selectActiveVenue);
  const venueId = routeParams.venueId || activeVenue.venueId;
  const { isMsInventoryVenue } = yield select(selectVenueByVenueId, venueId);
  if (isMsInventoryVenue) {
    yield all([
      call(doFetchSchedulesClasses, client),
      call(doFetchInstructors, client),
      call(doFetchMsInventoryVenues, client),
    ]);
  }
}

export function* handleRouteEnter(client, action) {
  yield call(waitForAppInit);

  const routeParams = yield select(selectRouteParams);
  const activeVenue = yield select(selectActiveVenue);
  const venueId = action.venueId || routeParams.venueId || activeVenue.venueId;
  const { isMsInventoryVenue } = yield select(selectVenueByVenueId, venueId);
  let { startDate } = routeParams;
  let newPath;

  if (isMsInventoryVenue) {
    if (!startDate) {
      startDate = yield call(getStartDateOrToday);
      newPath = makePath.schedules({ venueId, startDate });
    }
    yield all([
      call(doFetchSchedulesInstances, client, { shouldRefreshData: true }),
      call(doFetchSchedulesClasses, client),
      call(doFetchInstructors, client),
    ]);
  } else {
    newPath = makePath.schedules({ venueId });
  }

  if (newPath) {
    yield call(client.navigate, newPath);
  }
}

export function* handleActiveVenueChange(client, action) {
  const { venueId } = action;
  const newPath = makePath.schedules({ venueId });

  yield put(setSelectedScheduleInstanceId({ instanceId: null }));
  yield call(client.navigate, newPath);

  yield delay(1);
  yield call(handleRouteEnter, client, { venueId });
}

export function* watchSchedulesActions(client) {
  yield takeEvery(FETCH_SCHEDULE, doFetchSchedule, client);
  yield takeEvery(FETCH_SCHEDULES_INSTANCES, doFetchSchedulesInstances, client);
  yield takeEvery(FETCH_INSTRUCTORS, doFetchInstructors, client);
  yield takeEvery(SET_SCHEDULES_BOARD_CLASS_FILTERS, doFetchSchedulesInstances, client, { shouldRefreshData: true });
  yield takeEvery(SET_SCHEDULES_BOARD_DURATIONS_FILTERS, doFetchSchedulesInstances, client, {
    shouldRefreshData: true,
  });
  yield takeEvery(SET_SCHEDULES_BOARD_INSTRUCTOR_FILTERS, doFetchSchedulesInstances, client, {
    shouldRefreshData: true,
  });
  yield takeEvery(SET_SCHEDULES_BOARD_STATUS_FILTERS, doFetchSchedulesInstances, client, { shouldRefreshData: true });
  yield takeEvery(SET_SCHEDULES_BOARD_TYPE_FILTERS, doFetchSchedulesInstances, client, { shouldRefreshData: true });
  yield takeEvery(FETCH_SCHEDULES_CLASSES, doFetchSchedulesClasses, client);
  yield takeEvery(CREATE_CLASS, doCreateClass, client);
  yield takeEvery(CREATE_INSTRUCTOR, doCreateInstructor, client);
  yield takeEvery(CREATE_SCHEDULE, doCreateSchedule, client);
  yield takeEvery(UPDATE_SCHEDULE, doUpdateSchedule, client);
  yield takeEvery(CANCEL_SCHEDULE_INSTANCE, doCancelScheduleInstance, client);
  yield takeEvery(SET_SELECTED_INSTANCE_ID, doSetSelectedInstanceId, client);
  yield takeEvery(DELETE_SCHEDULE, doDeleteSchedule, client);
  yield takeEvery(ACTIVE_VENUE_CHANGED, handleActiveVenueChange, client);
  yield takeEvery(UPDATE_SCHEDULE_START_DATE, doUpdateStartDate, client);
  yield takeEvery(UPDATE_SCHEDULE_INSTANCE, doUpdateScheduleInstance, client);
}
